import React from 'react';
import { TableContainer, Card, Table, TableBody, TableCell, TableRow, Box } from '@mui/material';
import IconTooltip from './IconTooltip';
import renderPercentage from './renderPercentage';
import { useTheme } from '@mui/material/styles';
import ReportsCardHeader from './cards/ReportsCardHeader';
import ReportsCardContent from './cards/ReportsCardContent';
import DistributionChart from './charts/DistributionChart';
import { grey } from '@mui/material/colors';

const NewCampaignSummary = ({ campaign, subscriberStatus, onChangeSubscriberStatus }) => {
    const theme = useTheme();
    const unopened = campaign.sent - campaign.uniqueOpens - campaign.bounced;

    const chartData = [
        {
            value: campaign.uniqueOpens,
            name: 'Unique Opens',
            colour: theme.palette.success.light
        },
        {
            value: unopened,
            name: 'Unopened',
            colour: grey[300]
        },
        {
            value: campaign.bounced,
            name: 'Bounced',
            colour: theme.palette.warning.main
        },
        {
            value: campaign.failed,
            name: 'Failed',
            colour: theme.palette.error.main
        }
    ];

    return (
        <Card variant="outlined">
            <ReportsCardHeader title="Campaign Summary" />
            <ReportsCardContent>
                <Box sx={{ display: 'flex' }}>
                    <TableContainer sx={{ flexGrow: 1 }}>
                        <Table size="small">
                            <TableBody>
                                <TableRow
                                    hover={true}
                                    selected={subscriberStatus === 'selected'}
                                    onClick={() => onChangeSubscriberStatus('selected')}
                                >
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Selected</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="Number of contacts selected to be sent to" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.selected}</TableCell>
                                    <TableCell align="right">{renderPercentage(campaign.selected, campaign.selected)}</TableCell>
                                </TableRow>
                                <TableRow
                                    hover={true}
                                    selected={subscriberStatus === 'sent'}
                                    onClick={() => onChangeSubscriberStatus('sent')}
                                >
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Sent</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="Total number of emails successfully sent" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.sent}</TableCell>
                                    <TableCell align="right">{renderPercentage(campaign.sent, campaign.selected)}</TableCell>
                                </TableRow>
                                <TableRow
                                    hover={true}
                                    selected={subscriberStatus === 'delivered'}
                                    onClick={() => onChangeSubscriberStatus('delivered')}
                                >
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Delivered</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="Total number of emails successfully delivered (sent minus bounced)" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.sent - campaign.bounced}</TableCell>
                                    <TableCell align="right">{renderPercentage((campaign.sent - campaign.bounced), campaign.selected)}</TableCell>
                                </TableRow>
                                <TableRow
                                    hover={true}
                                    selected={subscriberStatus === 'opened'}
                                    onClick={() => onChangeSubscriberStatus('opened')}
                                >
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Unique Opens</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="The number of unique contacts to have opened the campaign at least once" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.uniqueOpens}</TableCell>
                                    <TableCell align="right">{renderPercentage(campaign.uniqueOpens, campaign.selected)}</TableCell>
                                </TableRow>
                                <TableRow
                                    hover={true}
                                    selected={subscriberStatus === 'clicked'}
                                    onClick={() => onChangeSubscriberStatus('clicked')}
                                >
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Unique Clicks</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="The number of unique contacts to have clicked a link or links in the campaign at least once" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.uniqueClicks}</TableCell>
                                    <TableCell align="right">{renderPercentage(campaign.uniqueClicks, campaign.uniqueOpens)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Total Opens</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="The number of times the campaign was opened, including multiple opens by the same contact" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.totalOpens}</TableCell>
                                    <TableCell align="right">{renderPercentage(campaign.totalOpens, campaign.selected)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Total Clicks</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="The number of times links in the campaign were clicked, including multiple clicks by the same contact" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.totalClicks}</TableCell>
                                    <TableCell align="right">{renderPercentage(campaign.totalClicks, campaign.totalOpens)}</TableCell>
                                </TableRow>
                                <TableRow
                                    hover={true}
                                    selected={subscriberStatus === 'unopened'}
                                    onClick={() => onChangeSubscriberStatus('unopened')}
                                >
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Unopened</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="Contacts who have not opened the campaign" />
                                    </TableCell>
                                    <TableCell align="right">{unopened}</TableCell>
                                    <TableCell align="right">{renderPercentage(unopened, campaign.selected)}</TableCell>
                                </TableRow>
                                <TableRow
                                    hover={true}
                                    selected={subscriberStatus === 'bounced'}
                                    onClick={() => onChangeSubscriberStatus('bounced')}
                                >
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Bounced</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="Total number of emails that couldn't be delivered to a contact" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.bounced}</TableCell>
                                    <TableCell align="right">{renderPercentage(campaign.bounced, campaign.selected)}</TableCell>
                                </TableRow>
                                <TableRow
                                    hover={true}
                                    selected={subscriberStatus === 'failed'}
                                    onClick={() => onChangeSubscriberStatus('failed')}
                                >
                                    <TableCell component="th" sx={{ whiteSpace: 'nowrap' }}>Failed</TableCell>
                                    <TableCell width="100%">
                                        <IconTooltip title="Total number of emails that couldn't be sent" />
                                    </TableCell>
                                    <TableCell align="right">{campaign.failed}</TableCell>
                                    <TableCell align="right">{renderPercentage(campaign.failed, campaign.selected)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ marginLeft: 3, width: '300px' }}>
                        <DistributionChart
                            data={chartData}
                        />
                    </Box>
                </Box>
            </ReportsCardContent>
        </Card>
    );
};

export default NewCampaignSummary;